import { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';

export interface AssetFile {
  file: File;
  slotId: string;
  preview?: string;
  storageUrl?: string;
  uploaded: boolean;
}

export function useAssetUpload() {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);

  /**
   * Upload a single asset to Firebase Storage and return the download URL.
   * Does NOT manage the shared `uploading` flag — that is the responsibility
   * of the caller (uploadMultiple) so the flag stays true for the entire batch.
   */
  const uploadAsset = async (file: File, slotId: string, projectId: string): Promise<string> => {
    console.log(`[useAssetUpload] upload start — slot="${slotId}" file="${file.name}" size=${file.size}`);
    setUploadProgress((prev) => ({ ...prev, [slotId]: 0 }));

    const fileExtension = file.name.split('.').pop();
    const storagePath = `assets/${projectId}/original/${slotId}.${fileExtension}`;
    const storageRef = ref(storage, storagePath);

    try {
      await uploadBytes(storageRef, file);
      console.log(`[useAssetUpload] bytes uploaded — slot="${slotId}" file="${file.name}"`);

      console.log(`[useAssetUpload] fetching download URL — slot="${slotId}"`);
      const downloadURL = await getDownloadURL(storageRef);
      console.log(`[useAssetUpload] download URL obtained — slot="${slotId}" url="${downloadURL}"`);

      setUploadProgress((prev) => ({ ...prev, [slotId]: 100 }));
      return downloadURL;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Upload all assets sequentially (one at a time) so that a single slow or
   * failing upload cannot starve the network or leave the UI in an indefinite
   * loading state.  Each file is attempted independently — a failure on one
   * file is recorded but does not abort the remaining files.
   */
  const uploadMultiple = async (
    assets: AssetFile[],
    projectId: string
  ): Promise<AssetFile[]> => {
    const uploadedAssets: AssetFile[] = [];

    setUploading(true);
    setError(null);

    try {
      for (const asset of assets) {
        try {
          const storageUrl = await uploadAsset(asset.file, asset.slotId, projectId);
          console.log(`[useAssetUpload] upload success — slot="${asset.slotId}"`);
          uploadedAssets.push({ ...asset, storageUrl, uploaded: true });
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Upload failed';
          console.error(`[useAssetUpload] upload failure — slot="${asset.slotId}" error="${msg}"`);
          setError(msg);
          uploadedAssets.push({ ...asset, uploaded: false });
        }
      }
    } finally {
      // Always clear the loading flag, even if an unexpected error escapes the
      // inner try/catch, so the UI is never left in an infinite loading state.
      setUploading(false);
      console.log(`[useAssetUpload] uploadMultiple finished — ${uploadedAssets.filter((a) => a.uploaded).length}/${uploadedAssets.length} succeeded`);
    }

    return uploadedAssets;
  };

  return {
    uploadAsset,
    uploadMultiple,
    uploading,
    uploadProgress,
    error,
  };
}
